import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Clock, ArrowRight, ChevronLeft } from "lucide-react";
import SEO from "../seo/SEO";
import { useT } from "../seo/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ORGANIZATION_SCHEMA, buildBreadcrumbSchema, SITE_DOMAIN } from "../seo/seoConfig";
import { BLOG_POSTS } from "../seo/blogPostsConfig";

const heading = { fontFamily: "'Satoshi', sans-serif" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndexPage() {
  const { t, lang } = useT();
  const [dynamicPosts, setDynamicPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/blog/list`, { params: { language: lang } })
      .then((res) => { if (!cancelled) setDynamicPosts(res.data?.posts || []); })
      .catch(() => { if (!cancelled) setDynamicPosts([]); });
    return () => { cancelled = true; };
  }, [lang]);

  const categories = [...new Set(BLOG_POSTS.map(p => p.category))];

  return (
    <div className="bg-white min-h-screen" data-testid="blog-index">
      <SEO
        title="InvoiceSetu Blog — GST, Invoicing & Cash Flow Guides for Indian Businesses"
        description="Practical guides on GST invoicing, quotations, payment collection, freelancing and Indian compliance. Plain-English. No jargon."
        path={lang === "en" ? "/blog" : `/${lang}/blog`}
        multilingual
        schemas={[
          ORGANIZATION_SCHEMA,
          buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }]),
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "InvoiceSetu Blog",
            url: `${SITE_DOMAIN}/blog`,
            blogPost: BLOG_POSTS.map(p => ({
              "@type": "BlogPosting",
              headline: p.title,
              datePublished: p.published,
              dateModified: p.updated,
              author: { "@type": "Organization", name: "InvoiceSetu" },
              url: `${SITE_DOMAIN}/blog/${p.slug}`,
              description: p.description,
            })),
          },
        ]}
      />

      <div className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> InvoiceSetu
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm" data-testid="blog-cta">{t("nav.signUp")}</Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <p className="text-xs font-bold text-blue-600 uppercase tracking-[0.18em] mb-2">InvoiceSetu Learning Center</p>
        <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-3" style={heading}>
          Guides for Indian Businesses
        </h1>
        <p className="text-base text-slate-600 max-w-2xl mb-8">
          GST, invoicing, quotations, cash flow and freelancing — explained in plain English. No jargon, no fluff.
        </p>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map(c => (
            <span key={c} className="text-xs font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 rounded-full px-3 py-1.5">
              {c}
            </span>
          ))}
        </div>

        {/* Featured post */}
        {BLOG_POSTS[0] && (
          <Link
            to={`/blog/${BLOG_POSTS[0].slug}`}
            className="block bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-3xl p-8 sm:p-10 mb-10 hover:shadow-lg transition-shadow"
            data-testid="blog-featured"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200 mb-3 inline-block">Featured · {BLOG_POSTS[0].category}</span>
            <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight" style={heading}>{BLOG_POSTS[0].title}</h2>
            <p className="text-blue-100 mb-5 max-w-2xl">{BLOG_POSTS[0].hero}</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        )}

        {/* Dynamic posts published from SEO Dashboard */}
        {dynamicPosts.length > 0 && (
          <div className="mb-12" data-testid="blog-dynamic-posts">
            <h2 className="text-xl font-black text-slate-900 mb-4" style={heading}>Latest from the InvoiceSetu Team</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {dynamicPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="bg-white border border-slate-100 rounded-2xl p-5 hover:border-blue-200 hover:shadow-sm transition-all"
                  data-testid={`blog-dynamic-${p.slug}`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600">NEW · {p.category}</span>
                  <h3 className="font-bold text-slate-900 mt-2 mb-2 leading-snug" style={heading}>{p.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-2 mb-4">{p.excerpt}</p>
                  <div className="flex items-center gap-3 text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {(p.created_at || "").slice(0, 10)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_minutes || 4} min</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Grid of remaining posts */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BLOG_POSTS.slice(1).map((p, i) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="bg-white border border-slate-100 rounded-2xl p-5 hover:border-blue-200 hover:shadow-sm transition-all"
              data-testid={`blog-card-${i}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-600">{p.category}</span>
              <h3 className="font-bold text-slate-900 mt-2 mb-2 leading-snug" style={heading}>{p.title}</h3>
              <p className="text-xs text-slate-500 line-clamp-2 mb-4">{p.hero}</p>
              <div className="flex items-center gap-3 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.published}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readMin} min</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <footer className="border-t border-slate-100 py-8 px-4 text-center text-xs text-slate-400">
        Powered by <Link to="/" className="text-blue-600 font-semibold">InvoiceSetu</Link>
      </footer>
    </div>
  );
}
